import { useState, useEffect } from 'react';
import axios from 'axios';

/**
 * Hook to manage current language selection for a user
 * Uses localStorage to persist language preference
 */
const useCurrentLanguage = (userId, defaultLanguage = 'en') => {
  const [currentLanguage, setCurrentLanguage] = useState(defaultLanguage);
  const [loading, setLoading] = useState(true);
  const [userLanguages, setUserLanguages] = useState([{ code: 'en', name: 'English', flag: '🇺🇸' }]);

  const ALL_LANGUAGES = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'es', name: 'Spanish', flag: '🇪🇸' },
    { code: 'fr', name: 'French', flag: '🇫🇷' },
    { code: 'de', name: 'German', flag: '🇩🇪' },
    { code: 'it', name: 'Italian', flag: '🇮🇹' },
    { code: 'pt', name: 'Portuguese', flag: '🇵🇹' },
    { code: 'ru', name: 'Russian', flag: '🇷🇺' },
    { code: 'ja', name: 'Japanese', flag: '🇯🇵' },
    { code: 'ko', name: 'Korean', flag: '🇰🇷' },
    { code: 'zh', name: 'Chinese', flag: '🇨🇳' },
    { code: 'ar', name: 'Arabic', flag: '🇸🇦' },
    { code: 'hi', name: 'Hindi', flag: '🇮🇳' },
  ];

  // Get storage key for user-specific language preference
  const getStorageKey = () => {
    return userId ? `language_pref_${userId}` : 'selectedLanguage';
  };

  // Load language preference from localStorage
  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    const fetchUserLanguages = async () => {
      try {
        const res = await axios.get(`/api/language/${userId}`);
        const codes = res.data?.languages || ['en'];

        // Map codes to full language objects, always include 'en'
        const langSet = new Set(['en', ...codes]);
        const langs = ALL_LANGUAGES.filter(l => langSet.has(l.code));
        setUserLanguages(langs.length > 0 ? langs : [{ code: 'en', name: 'English', flag: '🇺🇸' }]);

        const savedLanguage = localStorage.getItem(getStorageKey());
        if (savedLanguage && langSet.has(savedLanguage)) {
          setCurrentLanguage(savedLanguage);
        } else {
          // Always default to 'en' if no saved preference
          setCurrentLanguage('en');
          localStorage.setItem(getStorageKey(), 'en');
        }
      } catch (error) {
        console.error('Error fetching user languages:', error);
        setUserLanguages([{ code: 'en', name: 'English', flag: '🇺🇸' }]);
        setCurrentLanguage('en');
      } finally {
        setLoading(false);
      }

    };

    fetchUserLanguages();
  }, [userId]);

  // Update language and save to localStorage
  const updateLanguage = (newLanguage) => {
    try {
      setCurrentLanguage(newLanguage);
      if (userId) {
        localStorage.setItem(getStorageKey(), newLanguage);
      }
    } catch (error) {
      console.error('Error saving language preference:', error);
    }
  };

  // Get available languages (can be extended to fetch from API)
  const getAvailableLanguages = () => userLanguages;

  // Get language display name
  const getLanguageName = (languageCode) => {
    const language = ALL_LANGUAGES.find(lang => lang.code === languageCode);
    return language ? language.name : languageCode.toUpperCase();
  };

  // Get language flag
  const getLanguageFlag = (languageCode) => {
    const language = ALL_LANGUAGES.find(lang => lang.code === languageCode);
    return language ? language.flag : '🌍';
  };

  return {
    currentLanguage,
    updateLanguage,
    loading,
    getAvailableLanguages,
    getLanguageName,
    getLanguageFlag,
  };
};

export default useCurrentLanguage;